import { NextItemsArea } from 'app/types/cards/NextItemsArea';
import { createUrl } from 'app/utils';
import { Box, Button, ButtonIcon, Flex, Section, Typography } from 'components';
import { contentsTypes } from 'features/contents';
import { DashboardAreaColorEnum, DashboardVisualizationTypeEnum } from 'features/dashboard-new';

import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ProductsCardNew } from '..';

interface NextItemsProps {
  nextItems: NextItemsArea;
}

const ContentWrapper = styled(Flex)`
  position: relative;

  &::before {
    content: '';
    display: block;
    width: 1px;
    height: 100%;
    background: ${({ theme }) => theme.colors.gray_600};
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const HatBullet = styled.i`
  position: absolute;
  display: block;
  background: ${({ theme }) => theme.colors.dark_400};
  position: absolute;
  border-radius: 50%;

  &::before {
    content: '';
    display: block;
    border: 1px solid ${({ theme }) => theme.colors.gray_600};
    border-radius: 50%;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 20px;
      height: 20px;
      left: -29px;
      top: -2px;

      &::before {
        width: 10px;
        height: 10px;
      }

      ${mediaQueries.tablet} {
        width: 26px;
        height: 26px;
        left: -52px;
        top: -4px;

        &::before {
          width: 13px;
          height: 13px;
        }
      }
    `;
  }}
`;

const NextItems = ({ nextItems }: NextItemsProps) => {
  const { nextLives, ...nextItem } = nextItems;

  const hasItem = !!Object.keys(nextItem).length;

  return (
    <Flex bg="dark_400">
      <Section flexWrap={{ m: 'wrap', d: 'nowrap' }} justifyContent={hasItem ? 'space-between' : 'center'}>
        {hasItem ? (
          <Flex pr={{ d: 50 }}>
            <Flex flexDirection="column" pt={{ m: 30, t: 40, d: 80 }}>
              <ContentWrapper flexGrow={0} alignItems="flex-start" pb={{ t: 40 }} pl={{ m: 20, t: 40 }} flexDirection="column">
                <Box position="relative">
                  <HatBullet />
                  <Typography type="type4" color="white25" upper>
                    {nextItem?.productName}
                  </Typography>
                </Box>

                <Typography type="type6" color="white" fontWeight="bold" lineHeight="1.25" mt={2}>
                  {nextItem?.currentOrNextContentName}
                </Typography>

                <Flex flexGrow={0} flexWrap={{ m: 'wrap', t: 'nowrap' }} mt={{ m: 3, t: 5 }}>
                  {Boolean(nextItem?.currentContentUuid) && (
                    <Box mr={{ t: '10px' }}>
                      <Link
                        to={
                          nextItem?.currentContentProductClassTimelineUuid
                            ? nextItem?.productClassUuid
                              ? createUrl('contentClassDetails', [
                                  nextItem?.productSlug,
                                  nextItem?.productClassUuid,
                                  nextItem?.currentContentProductClassTimelineUuid,
                                  nextItem?.currentContentUuid,
                                  contentsTypes(nextItem?.currentContentType, nextItem?.currentExternalDocumentType),
                                ])
                              : createUrl('contentDetails', [
                                  nextItem?.productSlug,
                                  nextItem?.currentContentProductClassTimelineUuid,
                                  nextItem?.currentContentUuid,
                                  contentsTypes(nextItem?.currentContentType, nextItem?.currentExternalDocumentType),
                                ])
                            : nextItem?.productClassUuid
                            ? createUrl('productContentClassDetails', [
                                nextItem?.productSlug,
                                nextItem?.productClassUuid,
                                '',
                                nextItem?.currentContentUuid,
                                contentsTypes(nextItem?.currentContentType, nextItem?.currentExternalDocumentType),
                              ])
                            : createUrl('productContentDetails', [
                                nextItem?.productSlug,
                                '',
                                nextItem?.currentContentUuid,
                                contentsTypes(nextItem?.currentContentType, nextItem?.currentExternalDocumentType),
                              ])
                        }
                      >
                        <Button minWidth={200}>
                          {/* {nextItem?.firstContent ? 'Iniciar' : 'Continuar'} */}
                          Continuar
                        </Button>
                      </Link>
                    </Box>
                  )}

                  {nextItem?.currentContentUuid !== nextItem?.nextContentUuid && Boolean(nextItem?.nextContentUuid) && (
                    <Box mt={{ m: '10px', t: 0 }} mb={{ m: 30, t: 0 }}>
                      <Link
                        to={createUrl('contentClassDetails', [
                          nextItem?.productSlug,
                          nextItem?.productClassUuid,
                          nextItem?.nextContentProductClassTimelineUuid,
                          nextItem?.nextContentUuid,
                          contentsTypes(nextItem?.nextContentType, nextItem?.nextExternalDocumentType),
                        ])}
                      >
                        <Button minWidth={200} outline>
                          Próxima aula
                        </Button>
                      </Link>
                    </Box>
                  )}
                </Flex>
              </ContentWrapper>

              <Box pl={{ m: 20, t: 40 }} pb={{ m: 20, t: 40 }} flexGrow={0} position="relative">
                <Link to={createUrl('productClassDetails', [nextItem?.productSlug, nextItem?.productClassUuid])}>
                  <ButtonIcon
                    icon="chevronDown"
                    size="large"
                    position="absolute"
                    left={-11}
                    color="primary_400"
                    bg="transparent"
                    areaLabel="Ver todas as aulas"
                  />

                  <Typography color="primary_400" type="type1" fontWeight="bold" upper>
                    Ver todas as aulas
                  </Typography>
                </Link>
              </Box>
            </Flex>
          </Flex>
        ) : (
          <div />
        )}

        {!!nextLives?.length && (
          <Flex
            flexDirection={{ m: 'column', t: 'row' }}
            flexGrow={0}
            pt={{ m: '10px', t: 30, d: 40 }}
            pb={{ m: 30, t: 40, d: 40 }}
            style={{ gap: 16 }}
            flexWrap={{ m: 'wrap', t: 'nowrap' }}
          >
            {nextLives.map((card, index) => {
              return (
                <Flex key={`sheet-nextlives-${index.toString()}-${card.uuid}`}>
                  <ProductsCardNew
                    card={card}
                    dashboardAreaColor={DashboardAreaColorEnum.Dark}
                    dashboardVisualizationType={DashboardVisualizationTypeEnum.Cinema}
                  />
                </Flex>
              );
            })}
          </Flex>
        )}
      </Section>
    </Flex>
  );
};

export default NextItems;
